import React from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export const Contact = () => {
  const contactInfo = [
    {
      icon: MapPin,
      title: 'Dirección',
      content: 'Av. Goyeneche 101, Arequipa 04001',
      link: 'https://www.google.com/maps/place/MIT+Instituto+de+Formaci%C3%B3n+Digital/@-16.402065,-71.529772,17z/'
    },
    {
      icon: Phone,
      title: 'Teléfono',
      content: '+51 944 748 443',
      link: 'tel:+51944748443'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'admision@institutomit.edu.pe',
      link: 'mailto:admision@institutomit.edu.pe'
    },
    {
      icon: Clock,
      title: 'Horario de Atención',
      content: 'Lunes a Viernes: 8:00 AM - 8:00 PM\nSábados: 9:00 AM - 1:00 PM',
      link: null
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-red-50 py-20 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Contáctanos</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Estamos aquí para resolver tus dudas y ayudarte a dar el siguiente paso en tu carrera profesional
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Card key={index} className="hover-lift">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-mit-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-7 h-7 text-mit-blue" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                    {item.link ? (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-mit-blue transition-colors text-sm whitespace-pre-line"
                      >
                        {item.content}
                      </a>
                    ) : (
                      <p className="text-gray-600 text-sm whitespace-pre-line">{item.content}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Mapa */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Encuéntranos</h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Visítanos en nuestras instalaciones en Arequipa. Estamos ubicados en una zona céntrica y de fácil acceso.
              </p>

              <Card className="overflow-hidden mb-6">
                <div className="w-full h-96">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3692.8890391626396!2d-71.53156312495081!3d-16.40206533821148!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x91424bdf7e43ed91%3A0xc3b97729facc781c!2sMIT%20Instituto%20de%20Formaci%C3%B3n%20Digital!5e1!3m2!1ses!2spe!4v1770140363000!5m2!1ses!2spe" 
                    width="100%" 
                    height="100%" 
                    style={{border: 0}} 
                    allowFullScreen="" 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Mapa de ubicación MIT"
                  ></iframe>
                </div>
              </Card>

              <Card className="hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-8 h-8 text-mit-blue flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Dirección Completa</h3>
                      <p className="text-gray-600 mb-4">
                        Av. Goyeneche 101, Arequipa 04001, Perú
                      </p>
                      <a
                        href="https://www.google.com/maps/place/MIT+Instituto+de+Formaci%C3%B3n+Digital/@-16.402065,-71.529772,17z/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="outline" className="border-mit-blue text-mit-blue hover:bg-mit-blue hover:text-white">
                          Ver en Google Maps
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Otras formas de contacto */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Otras Formas de Contacto</h2>
              
              {/* WhatsApp */}
              <Card className="mb-6 hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 mb-2">WhatsApp</h3>
                      <p className="text-gray-600 text-sm mb-4">
                        Respuesta inmediata a tus consultas. Escríbenos y te atenderemos en el menor tiempo posible.
                      </p>
                      <a
                        href="https://wa.me/51944748443?text=Hola, visité la web y me gustaría mas información."
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button className="bg-green-600 hover:bg-green-700 text-white">
                          Chatear por WhatsApp
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Teléfono */}
              <Card className="mb-6 hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-mit-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-mit-blue" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 mb-2">Llámanos</h3>
                      <p className="text-gray-600 text-sm mb-4">
                        Contáctanos directamente para recibir asesoría personalizada sobre nuestras carreras.
                      </p>
                      <a
                        href="tel:+51944748443"
                      >
                        <Button variant="outline" className="border-mit-blue text-mit-blue hover:bg-mit-blue hover:text-white">
                          +51 944 748 443
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Email */}
              <Card className="hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-mit-red/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-mit-red" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 mb-2">Correo Electrónico</h3>
                      <p className="text-gray-600 text-sm mb-4">
                        Envíanos un correo con tus consultas y te responderemos a la brevedad.
                      </p>
                      <a
                        href="mailto:admision@institutomit.edu.pe"
                      >
                        <Button variant="outline" className="border-mit-red text-mit-red hover:bg-mit-red hover:text-white">
                          admision@institutomit.edu.pe
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
